import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { COUNTRIES } from '../lib/signup-helpers';
import { 
  User, 
  Mail, 
  Lock, 
  Users, 
  Plus, 
  Trash2, 
  Loader2,
  Shield,
  UserPlus,
  LogOut,
  Building,
  Bell,
  Send,
  Target,
  RotateCcw,
  CreditCard,
  Receipt,
  Download,
  Calendar,
  ArrowUpRight,
  Zap,
  Globe,
  Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const { subscription, plan, planName, refreshSubscription } = useSubscription();
  const navigate = useNavigate();
  
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [company, setCompany] = useState(user?.company || '');
  const [country, setCountry] = useState(user?.country || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [savingBusiness, setSavingBusiness] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Team state
  const [team, setTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamName, setTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(true);

  // Notification state
  const [notifPref, setNotifPref] = useState('all');
  const [weeklyReport, setWeeklyReport] = useState(true);
  const [savingNotif, setSavingNotif] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [loadingNotif, setLoadingNotif] = useState(true);

  // Scoring weights state
  const [weights, setWeights] = useState({ intent: 25, budget: 20, authority: 15, timeline: 15, icp_fit: 15, risk: 10 });
  const [isCustomWeights, setIsCustomWeights] = useState(false);
  const weightsTotal = useMemo(
    () => Object.values(weights).reduce((s, v) => s + v, 0),
    [weights]
  );
  const [savingWeights, setSavingWeights] = useState(false);
  const [loadingWeights, setLoadingWeights] = useState(true);

  // Billing state
  const [billingHistory, setBillingHistory] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [billingDetails, setBillingDetails] = useState({
    company_name: '', address_line1: '', address_line2: '',
    city: '', state: '', postal_code: '', country: '', vat_number: '', billing_email: '',
  });
  const [editingBilling, setEditingBilling] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setCompany(user.company || '');
      setCountry(user.country || '');
      setPhone(user.phone || '');
      fetchTeam();
      fetchNotificationSettings();
      fetchBillingHistory();
      if (user.role === 'manager') fetchScoringWeights();
      else setLoadingWeights(false);
    }
  }, [user]);

  const fetchTeam = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/my-team`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeam(data.team);
        setTeamMembers(data.members || []);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoadingTeam(false);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifPref(data.email_notifications || 'all');
        setWeeklyReport(data.weekly_report !== false);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setLoadingNotif(false);
    }
  };

  const fetchScoringWeights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scoring/weights`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setWeights(data.weights);
        setIsCustomWeights(data.is_custom);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingWeights(false); }
  };

  const fetchBillingHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscription/billing-history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBillingHistory(data);
        if (data.billing_details) {
          setBillingDetails(prev => ({ ...prev, ...data.billing_details }));
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoadingBilling(false); }
  };

  const saveBillingDetails = async () => {
    setSavingBilling(true);
    try {
      const res = await fetch(`${API_URL}/api/subscription/billing-details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(billingDetails),
      });
      if (!res.ok) throw new Error('Failed to save');
      const data = await res.json();
      toast.success(data.message);
      setEditingBilling(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingBilling(false);
    }
  };

  const downloadInvoice = (event) => {
    const date = new Date(event.date);
    const invoiceId = `INV-${date.getFullYear()}${(date.getMonth()+1).toString().padStart(2,'0')}${date.getDate().toString().padStart(2,'0')}-${Math.random().toString(36).slice(2,6).toUpperCase()}`;
    const bd = billingDetails;
    const dateStr = date.toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
    const dueDate = new Date(date); dueDate.setDate(dueDate.getDate() + 14);
    const dueStr = dueDate.toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
    const amt = Number(event.amount || 0);
    const subtotal = amt;
    const vatRate = 0; // Tax handled by payment processor
    const vatAmount = 0;
    const total = subtotal + vatAmount;
    const origin = window.location.origin;

    const addressLines = [
      bd.address_line1,
      bd.address_line2,
      [bd.city, bd.state, bd.postal_code].filter(Boolean).join(', '),
      bd.country,
    ].filter(Boolean);

    const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"/>
<title>Invoice ${invoiceId} — Ludy</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin:0; padding:0; background:#f4f1ea; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; color:#1c1c1c; }
  .sheet { width: 210mm; min-height: 297mm; margin: 20px auto; background:#ffffff; padding: 22mm 20mm; position: relative; box-shadow: 0 10px 40px rgba(0,0,0,0.08); }
  @media print { body { background:#fff; } .sheet { margin:0; box-shadow:none; } .no-print { display:none !important; } }
  .no-print { position: fixed; top: 20px; right: 20px; z-index: 1000; display: flex; gap: 8px; }
  .no-print button { background:#0E5944; color:#fff; border:none; padding:10px 20px; border-radius:8px; font-weight:600; cursor:pointer; font-size:13px; }
  .no-print button.secondary { background:#e6e2d6; color:#1c1c1c; }
  .hdr { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:24px; border-bottom: 2px solid #0E5944; }
  .brand { display:flex; flex-direction:column; gap:4px; }
  .brand-mark { color:#0E5944; font-family: Georgia, 'Times New Roman', serif; font-style: italic; font-weight: 700; font-size: 42px; line-height: 1; letter-spacing: 0.5px; }
  .brand-tag { color:#6b7280; font-size: 11px; letter-spacing: 2px; text-transform: uppercase; }
  .doc-meta { text-align:right; }
  .doc-label { font-size: 11px; letter-spacing: 3px; color:#C9A84C; font-weight: 700; text-transform: uppercase; }
  .doc-number { font-size: 22px; color:#0E5944; font-weight: 700; margin-top: 4px; font-family: Georgia, serif; }
  .grid { display:grid; grid-template-columns: 1fr 1fr; gap: 36px; margin: 32px 0 24px; }
  .col h4 { font-size: 10px; letter-spacing: 2.5px; text-transform: uppercase; color:#9ca3af; margin: 0 0 10px; font-weight: 600; }
  .col p { margin: 2px 0; font-size: 13px; line-height: 1.55; color:#374151; }
  .col .strong { color:#1c1c1c; font-weight: 600; font-size: 14px; }
  .meta-row { display:flex; justify-content:space-between; padding: 12px 16px; background:#f9f7f1; border-radius: 6px; margin-bottom: 8px; font-size: 12px; }
  .meta-row span:first-child { color:#6b7280; text-transform:uppercase; letter-spacing:1.5px; font-size:10px; font-weight:600; }
  .meta-row span:last-child { color:#1c1c1c; font-weight: 600; }
  table.items { width:100%; border-collapse: collapse; margin-top: 14px; }
  table.items thead th { text-align:left; padding: 12px 14px; font-size: 10px; letter-spacing: 2px; color:#ffffff; background:#0E5944; text-transform:uppercase; font-weight: 600; }
  table.items thead th:last-child { text-align:right; }
  table.items tbody td { padding: 16px 14px; font-size: 13px; border-bottom: 1px solid #f0ece2; color:#1c1c1c; }
  table.items tbody td:last-child { text-align:right; font-variant-numeric: tabular-nums; }
  .totals { width: 50%; margin-left: auto; margin-top: 20px; }
  .totals .row { display:flex; justify-content:space-between; padding: 8px 14px; font-size: 13px; color:#6b7280; }
  .totals .row.total { margin-top: 6px; padding: 14px; background: #0E5944; color: #ffffff; border-radius: 6px; font-size: 15px; font-weight: 700; }
  .totals .row.total .amt { color: #C9A84C; }
  .note { margin-top: 40px; padding: 16px 18px; border-left: 3px solid #C9A84C; background:#fbf8f0; font-size: 12px; color: #374151; line-height: 1.55; }
  .footer { position: absolute; left: 20mm; right: 20mm; bottom: 14mm; padding-top: 14px; border-top: 1px solid #e6e2d6; display:flex; justify-content:space-between; font-size: 10px; color: #9ca3af; letter-spacing: 1px; text-transform: uppercase; }
</style></head><body>
<div class="no-print">
  <button onclick="window.print()">Print / Save as PDF</button>
  <button class="secondary" onclick="window.close()">Close</button>
</div>
<div class="sheet">
  <div class="hdr">
    <div class="brand">
      <div class="brand-mark">Ludy</div>
      <div class="brand-tag">AI Sales Intelligence</div>
    </div>
    <div class="doc-meta">
      <div class="doc-label">Invoice</div>
      <div class="doc-number">#${invoiceId}</div>
    </div>
  </div>

  <div class="grid">
    <div class="col">
      <h4>From</h4>
      <p class="strong">Ludy</p>
      <p>AI Sales Intelligence Platform</p>
      <p>${origin.replace(/^https?:\/\//, '')}</p>
    </div>
    <div class="col">
      <h4>Bill to</h4>
      <p class="strong">${bd.company_name || user?.name || '—'}</p>
      ${addressLines.map(l => `<p>${l}</p>`).join('')}
      ${bd.vat_number ? `<p>VAT / Tax ID: <strong>${bd.vat_number}</strong></p>` : ''}
      <p>${bd.billing_email || user?.email || ''}</p>
    </div>
  </div>

  <div class="meta-row">
    <span>Issue date</span><span>${dateStr}</span>
  </div>
  <div class="meta-row">
    <span>Due date</span><span>${dueStr}</span>
  </div>
  <div class="meta-row">
    <span>Status</span><span style="color:#0E5944;">Paid</span>
  </div>

  <table class="items">
    <thead>
      <tr>
        <th style="width:55%;">Description</th>
        <th style="width:15%;">Qty</th>
        <th style="width:15%;">Unit</th>
        <th style="width:15%;">Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${event.description}</td>
        <td>1</td>
        <td>$${amt.toFixed(2)}</td>
        <td>$${amt.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>

  <div class="totals">
    <div class="row"><span>Subtotal</span><span>$${subtotal.toFixed(2)}</span></div>
    ${vatRate > 0 ? `<div class="row"><span>VAT (${vatRate}%)</span><span>$${vatAmount.toFixed(2)}</span></div>` : ''}
    <div class="row total"><span>Total due</span><span class="amt">$${total.toFixed(2)} USD</span></div>
  </div>

  <div class="note">
    Thank you for choosing Ludy. Payment has been processed successfully.
    If you need adjustments or a copy of the receipt, reply to your welcome email or contact support from your dashboard.
  </div>

  <div class="footer">
    <span>Invoice ${invoiceId}</span>
    <span>Page 1 / 1</span>
  </div>
</div>
<script>
  // Auto-focus the print dialog for quick PDF export
  setTimeout(() => { try { window.focus(); } catch(e){} }, 100);
</script>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (!w) {
      // Popup blocked — fall back to download
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceId}.html`;
      a.click();
    }
    // Revoke after a delay so the new tab has time to load
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success('Invoice opened — use Print / Save as PDF');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });
  };

  const handleSaveWeights = async () => {
    const total = Object.values(weights).reduce((s, v) => s + v, 0);
    if (Math.abs(total - 100) > 0.5) {
      toast.error(`Weights must sum to 100 (currently ${total})`);
      return;
    }
    setSavingWeights(true);
    try {
      const res = await fetch(`${API_URL}/api/scoring/weights`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ weights })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      setIsCustomWeights(true);
      toast.success('Scoring weights saved');
    } catch (e) { toast.error(e.message); }
    finally { setSavingWeights(false); }
  };

  const handleResetWeights = async () => {
    setSavingWeights(true);
    try {
      const res = await fetch(`${API_URL}/api/scoring/weights`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setWeights(data.weights);
        setIsCustomWeights(false);
        toast.success('Weights reset to defaults');
      }
    } catch (e) { toast.error(e.message); }
    finally { setSavingWeights(false); }
  };

  const handleSaveNotifications = async () => {
    setSavingNotif(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email_notifications: notifPref,
          weekly_report: weeklyReport
        })
      });
      if (!response.ok) throw new Error('Failed to save');
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingNotif(false);
    }
  };

  const handleSendWeeklyReport = async () => {
    setSendingWeekly(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/send-weekly-report`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to send');
      toast.success(data.message || 'Weekly report sent!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingWeekly(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim() })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update profile');
      }
      
      const updatedUser = await response.json();
      setUser(updatedUser);
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBusinessInfo = async () => {
    setSavingBusiness(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company: company.trim(),
          country: country.trim(),
          phone: phone.trim(),
        }),
      });
      if (!response.ok) throw new Error('Failed to update business info');
      const updatedUser = await response.json();
      setUser(updatedUser);
      toast.success('Business info updated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    
    setCreatingTeam(true);
    
    try {
      const response = await fetch(`${API_URL}/api/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: teamName.trim() })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create team');
      }
      
      toast.success('Team created!');
      setTeamName('');
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/teams/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: inviteEmail.trim() })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to invite');
      }
      
      toast.success('Member added to team!');
      setInviteEmail('');
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/teams/members/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to remove member');
      }
      
      toast.success('Member removed');
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLeaveTeam = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/leave`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to leave team');
      }
      
      toast.success('You have left the team');
      fetchTeam();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isGoogleAuth = user?.auth_provider === 'google';
  const isManager = user?.role === 'manager';

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Profile & Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account and team</p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary/10">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            {!isGoogleAuth && (
              <TabsTrigger value="security" className="data-[state=active]:bg-primary/10">
                <Lock className="w-4 h-4 mr-2" />
                Security
              </TabsTrigger>
            )}
            {isManager && (
              <TabsTrigger value="team" className="data-[state=active]:bg-primary/10">
                <Users className="w-4 h-4 mr-2" />
                Team
              </TabsTrigger>
            )}
            <TabsTrigger value="notifications" className="data-[state=active]:bg-primary/10">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-primary/10">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            {isManager && (
              <TabsTrigger value="scoring" className="data-[state=active]:bg-primary/10">
                <Target className="w-4 h-4 mr-2" />
                Scoring
              </TabsTrigger>
            )}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="mt-6 space-y-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Your Profile</CardTitle>
                <CardDescription>Update your personal information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar & Info */}
                <div className="flex items-center gap-6">
                  <Avatar className="w-20 h-20">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-secondary text-foreground text-xl">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground">{user?.name}</h3>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={user?.role === 'manager' ? 'badge-sql' : 'badge-mql'}>
                        {user?.role === 'manager' ? 'Manager' : 'Sales Rep'}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {isGoogleAuth ? 'Google Account' : 'Email Account'}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Edit Name */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Display Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="bg-background border-border max-w-sm"
                      data-testid="profile-name"
                    />
                  </div>
                  
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving || name === user?.name}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="save-profile"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Business Info */}
            <Card className="bg-card border-border/50" data-testid="business-info-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="w-5 h-5 text-primary" />
                  Business Info
                </CardTitle>
                <CardDescription>Used for invoices, billing context, and team directory</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="profile-company">Company</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="profile-company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Acme Inc."
                        className="pl-10 bg-background border-border"
                        data-testid="profile-company"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-country">Country</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
                      <select
                        id="profile-country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="h-10 w-full rounded-md border border-border bg-background pl-10 pr-3 text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-primary/50"
                        data-testid="profile-country"
                      >
                        <option value="">Select country</option>
                        {COUNTRIES.map(c => (
                          <option key={c.code} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 max-w-sm">
                  <Label htmlFor="profile-phone">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="profile-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="pl-10 bg-background border-border"
                      data-testid="profile-phone"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveBusinessInfo}
                  disabled={
                    savingBusiness ||
                    ((company || '') === (user?.company || '') &&
                     (country || '') === (user?.country || '') &&
                     (phone || '') === (user?.phone || ''))
                  }
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="save-business-info"
                >
                  {savingBusiness ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Business Info'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Team Info for Reps */}
            {!isManager && (
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="w-5 h-5 text-primary" />
                    Team Membership
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingTeam ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </div>
                  ) : team ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-foreground">{team.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive/10">
                              <LogOut className="w-4 h-4 mr-2" />
                              Leave Team
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card border-border">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Leave Team?</AlertDialogTitle>
                              <AlertDialogDescription>
                                You will no longer have access to team features. You can be invited back later.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleLeaveTeam}
                                className="bg-destructive text-white hover:bg-destructive/90"
                              >
                                Leave Team
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">You are not part of a team yet. Ask a manager to invite you.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Security Tab */}
          {!isGoogleAuth && (
            <TabsContent value="security" className="mt-6">
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Change Password</CardTitle>
                  <CardDescription>Update your password to keep your account secure</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 max-w-sm">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Current Password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-background border-border"
                      data-testid="current-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      className="bg-background border-border"
                      data-testid="new-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-background border-border"
                      data-testid="confirm-password"
                    />
                  </div>

                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="change-password"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Team Tab (Manager only) */}
          {isManager && (
            <TabsContent value="team" className="mt-6 space-y-6">
              {loadingTeam ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : !team ? (
                // Create Team
                <Card className="bg-card border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Create Your Team</CardTitle>
                    <CardDescription>Set up a team to invite sales reps and track their performance</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 max-w-sm">
                      <Label htmlFor="team-name">Team Name</Label>
                      <Input
                        id="team-name"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="e.g., Enterprise Sales Team"
                        className="bg-background border-border"
                        data-testid="team-name"
                      />
                    </div>
                    
                    <Button
                      onClick={handleCreateTeam}
                      disabled={creatingTeam}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="create-team"
                    >
                      {creatingTeam ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Create Team
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                // Manage Team
                <>
                  <Card className="bg-card border-border/50">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{team.name}</CardTitle>
                          <CardDescription>{teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}</CardDescription>
                        </div>
                        <Badge className="badge-sql">
                          <Shield className="w-3 h-3 mr-1" />
                          You are the manager
                        </Badge>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Invite Member */}
                  <Card className="bg-card border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-primary" />
                        Add Team Member
                      </CardTitle>
                      <CardDescription>Invite an existing user by their email address</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-4 max-w-md">
                        <Input
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="colleague@company.com"
                          className="bg-background border-border"
                          data-testid="invite-email"
                        />
                        <Button
                          onClick={handleInvite}
                          disabled={inviting}
                          className="bg-primary text-primary-foreground hover:bg-primary/90"
                          data-testid="invite-btn"
                        >
                          {inviting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Add'
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        The user must already have an account to be added to your team.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Team Members */}
                  <Card className="bg-card border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Team Members</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {teamMembers.map((member) => (
                          <div
                            key={member.user_id}
                            className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={member.picture} alt={member.name} />
                                <AvatarFallback className="bg-secondary text-foreground">
                                  {getInitials(member.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-foreground">{member.name}</p>
                                <p className="text-sm text-muted-foreground">{member.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs capitalize">
                                {member.role.replace('_', ' ')}
                              </Badge>
                              {member.user_id !== user.user_id && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="bg-card border-border">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Remove {member.name}?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        They will be removed from the team but their account and data will remain.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleRemoveMember(member.user_id)}
                                        className="bg-destructive text-white hover:bg-destructive/90"
                                      >
                                        Remove
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          )}

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-6 space-y-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Email Notifications</CardTitle>
                <CardDescription>Choose when to receive email alerts about call analyses</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loadingNotif ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      <Label className="text-base font-medium">Call Analysis Alerts</Label>
                      <div className="space-y-3">
                        {[
                          { value: 'all', label: 'All Notifications', desc: 'Receive email for every completed call analysis + high-risk alerts' },
                          { value: 'high_risk_only', label: 'High-Risk Only', desc: 'Only receive alerts when a deal has risk score above 60' },
                          { value: 'disabled', label: 'Disabled', desc: 'No email notifications for call analyses' }
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                              notifPref === option.value
                                ? 'border-primary bg-primary/5'
                                : 'border-border bg-background hover:bg-secondary/50'
                            }`}
                            data-testid={`notif-${option.value}`}
                          >
                            <input
                              type="radio"
                              name="notif-pref"
                              value={option.value}
                              checked={notifPref === option.value}
                              onChange={(e) => setNotifPref(e.target.value)}
                              className="mt-1 accent-primary"
                            />
                            <div>
                              <p className="font-medium text-foreground">{option.label}</p>
                              <p className="text-sm text-muted-foreground">{option.desc}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-medium">Weekly Manager Report</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Receive a weekly digest with top risky deals, top performing reps, and coaching summary
                        </p>
                      </div>
                      <button
                        onClick={() => setWeeklyReport(!weeklyReport)}
                        className={`relative w-12 h-7 rounded-full transition-colors ${
                          weeklyReport ? 'bg-primary' : 'bg-secondary border border-border'
                        }`}
                        data-testid="weekly-report-toggle"
                      >
                        <span
                          className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${
                            weeklyReport ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <Button
                        onClick={handleSaveNotifications}
                        disabled={savingNotif}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="save-notifications"
                      >
                        {savingNotif ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save Settings'
                        )}
                      </Button>
                      {isManager && (
                        <Button
                          variant="outline"
                          onClick={handleSendWeeklyReport}
                          disabled={sendingWeekly}
                          className="border-border"
                          data-testid="send-weekly-report"
                        >
                          {sendingWeekly ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Weekly Report Now
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scoring Weights Tab (Manager Only) */}
          {isManager && (
            <TabsContent value="scoring" className="mt-6 space-y-6">
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Scoring Weights</CardTitle>
                  <CardDescription>
                    Customize how deal scores are calculated. Weights must sum to 100%.
                    {isCustomWeights && <Badge className="ml-2 bg-primary/15 text-primary border border-primary/30">Custom</Badge>}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {loadingWeights ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : (
                    <>
                      <div className="space-y-4">
                        {[
                          { key: 'intent', label: 'Intent to Buy', desc: 'How strong are the buying signals?' },
                          { key: 'budget', label: 'Budget', desc: 'Is budget confirmed or discussed?' },
                          { key: 'authority', label: 'Authority', desc: 'Is the buyer a decision maker?' },
                          { key: 'timeline', label: 'Timeline', desc: 'How soon do they need a solution?' },
                          { key: 'icp_fit', label: 'ICP Fit', desc: 'How well does the prospect fit your ICP?' },
                          { key: 'risk', label: 'Risk', desc: 'How risky is this deal? (higher = less risk)' }
                        ].map(({ key, label, desc }) => (
                          <div key={key} className="flex items-center gap-4 p-3 rounded-lg bg-secondary/30 border border-border/50">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-foreground">{label}</p>
                              <p className="text-xs text-muted-foreground">{desc}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="range"
                                min="0"
                                max="50"
                                value={weights[key] || 0}
                                onChange={(e) => setWeights(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                                className="w-24 accent-primary"
                                data-testid={`weight-${key}`}
                              />
                              <span className="w-10 text-right text-sm font-mono font-bold text-foreground">{weights[key]}%</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-border/50">
                        <span className={`text-sm font-mono ${Math.abs(weightsTotal - 100) > 0.5 ? 'text-destructive' : 'text-success'}`}>
                          Total: {weightsTotal}%
                        </span>
                        <div className="flex gap-2">
                          {isCustomWeights && (
                            <Button
                              variant="outline"
                              onClick={handleResetWeights}
                              disabled={savingWeights}
                              className="border-border"
                              data-testid="reset-weights"
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Reset to Defaults
                            </Button>
                          )}
                          <Button
                            onClick={handleSaveWeights}
                            disabled={savingWeights || Math.abs(Object.values(weights).reduce((s, v) => s + v, 0) - 100) > 0.5}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                            data-testid="save-weights"
                          >
                            {savingWeights ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Save Weights
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6 mt-6" data-testid="billing-tab">
            {/* Current Plan */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Current Plan</CardTitle>
                      <CardDescription>Manage your subscription</CardDescription>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate('/pricing')} data-testid="billing-change-plan">
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                    Change Plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingBilling ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : billingHistory ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border/30">
                      <p className="text-xs text-muted-foreground mb-1">Plan</p>
                      <p className="text-lg font-bold text-foreground" data-testid="billing-plan-name">
                        {billingHistory.current_plan_name}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize">{billingHistory.billing_cycle}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border/30">
                      <p className="text-xs text-muted-foreground mb-1">Monthly Price</p>
                      <p className="text-lg font-bold text-foreground" data-testid="billing-price">
                        ${billingHistory.current_price}<span className="text-sm font-normal text-muted-foreground">/mo</span>
                      </p>
                      {billingHistory.billing_cycle === 'annual' && (
                        <p className="text-xs text-emerald-600">20% annual discount</p>
                      )}
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border/30">
                      <p className="text-xs text-muted-foreground mb-1">Next Billing</p>
                      <p className="text-lg font-bold text-foreground" data-testid="billing-next-date">
                        {billingHistory.next_billing_date ? formatDate(billingHistory.next_billing_date) : 'N/A'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {billingHistory.current_price > 0 ? 'Auto-renewal' : 'Free plan'}
                      </p>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                      <Building className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Billing Details</CardTitle>
                      <CardDescription>Company & invoicing information</CardDescription>
                    </div>
                  </div>
                  {!editingBilling && (
                    <Button size="sm" variant="outline" onClick={() => setEditingBilling(true)} data-testid="edit-billing-details">
                      {billingDetails.company_name ? 'Edit' : 'Add Details'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent data-testid="billing-details-section">
                {editingBilling ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Company Name</Label>
                        <Input
                          value={billingDetails.company_name}
                          onChange={e => setBillingDetails(p => ({ ...p, company_name: e.target.value }))}
                          placeholder="Acme Inc."
                          className="bg-secondary/50 border-border mt-1"
                          data-testid="billing-company-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Billing Email</Label>
                        <Input
                          value={billingDetails.billing_email}
                          onChange={e => setBillingDetails(p => ({ ...p, billing_email: e.target.value }))}
                          placeholder="billing@company.com"
                          className="bg-secondary/50 border-border mt-1"
                          data-testid="billing-email"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Address Line 1</Label>
                      <Input
                        value={billingDetails.address_line1}
                        onChange={e => setBillingDetails(p => ({ ...p, address_line1: e.target.value }))}
                        placeholder="123 Main Street"
                        className="bg-secondary/50 border-border mt-1"
                        data-testid="billing-address1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Address Line 2</Label>
                      <Input
                        value={billingDetails.address_line2}
                        onChange={e => setBillingDetails(p => ({ ...p, address_line2: e.target.value }))}
                        placeholder="Suite 100"
                        className="bg-secondary/50 border-border mt-1"
                        data-testid="billing-address2"
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">City</Label>
                        <Input
                          value={billingDetails.city}
                          onChange={e => setBillingDetails(p => ({ ...p, city: e.target.value }))}
                          placeholder="Belgrade"
                          className="bg-secondary/50 border-border mt-1"
                          data-testid="billing-city"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">State / Region</Label>
                        <Input
                          value={billingDetails.state}
                          onChange={e => setBillingDetails(p => ({ ...p, state: e.target.value }))}
                          placeholder="Serbia"
                          className="bg-secondary/50 border-border mt-1"
                          data-testid="billing-state"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Postal Code</Label>
                        <Input
                          value={billingDetails.postal_code}
                          onChange={e => setBillingDetails(p => ({ ...p, postal_code: e.target.value }))}
                          placeholder="11000"
                          className="bg-secondary/50 border-border mt-1"
                          data-testid="billing-postal"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Country</Label>
                        <Input
                          value={billingDetails.country}
                          onChange={e => setBillingDetails(p => ({ ...p, country: e.target.value }))}
                          placeholder="Serbia"
                          className="bg-secondary/50 border-border mt-1"
                          data-testid="billing-country"
                        />
                      </div>
                    </div>
                    <div className="sm:w-1/2">
                      <Label className="text-xs text-muted-foreground">VAT / Tax ID</Label>
                      <Input
                        value={billingDetails.vat_number}
                        onChange={e => setBillingDetails(p => ({ ...p, vat_number: e.target.value }))}
                        placeholder="RS123456789"
                        className="bg-secondary/50 border-border mt-1"
                        data-testid="billing-vat"
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button onClick={saveBillingDetails} disabled={savingBilling} className="bg-primary text-primary-foreground" data-testid="save-billing-details">
                        {savingBilling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Save Details
                      </Button>
                      <Button variant="outline" onClick={() => setEditingBilling(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : billingDetails.company_name ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border/30 space-y-1.5">
                      <p className="text-xs text-muted-foreground">Company</p>
                      <p className="text-sm font-medium text-foreground" data-testid="billing-display-company">{billingDetails.company_name}</p>
                      {billingDetails.billing_email && (
                        <p className="text-xs text-muted-foreground">{billingDetails.billing_email}</p>
                      )}
                      {billingDetails.vat_number && (
                        <p className="text-xs text-muted-foreground">VAT: {billingDetails.vat_number}</p>
                      )}
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50 border border-border/30 space-y-1.5">
                      <p className="text-xs text-muted-foreground">Address</p>
                      {billingDetails.address_line1 && <p className="text-sm text-foreground/90">{billingDetails.address_line1}</p>}
                      {billingDetails.address_line2 && <p className="text-sm text-foreground/90">{billingDetails.address_line2}</p>}
                      <p className="text-sm text-foreground/90">
                        {[billingDetails.city, billingDetails.state, billingDetails.postal_code].filter(Boolean).join(', ')}
                      </p>
                      {billingDetails.country && <p className="text-sm text-foreground/90">{billingDetails.country}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-6 text-center">
                    <Building className="w-10 h-10 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground mb-1">No billing details added</p>
                    <p className="text-xs text-muted-foreground/70">Add your company info for accurate invoices</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Method - Paddle */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Payment Method</CardTitle>
                    <CardDescription>Manage how you pay</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent data-testid="payment-method-section">
                {billingHistory?.payment_method ? (
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border/30">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-8 h-8 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">•••• •••• •••• {billingHistory.payment_method.last4}</p>
                        <p className="text-xs text-muted-foreground">Expires {billingHistory.payment_method.expiry}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">Update</Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-secondary border border-border flex items-center justify-center mb-3">
                      <CreditCard className="w-7 h-7 text-muted-foreground/40" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">No payment method on file</p>
                    <p className="text-xs text-muted-foreground/70 mb-4">Payment methods will be available once Paddle integration is active</p>
                    <Button size="sm" variant="outline" disabled className="opacity-50" data-testid="add-payment-method">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Payment Method
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Billing History */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Billing History</CardTitle>
                    <CardDescription>Past transactions and invoices</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent data-testid="billing-history-section">
                {loadingBilling ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : billingHistory?.events?.length > 0 ? (
                  <div className="space-y-0">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-border/30">
                      <div className="col-span-2">Date</div>
                      <div className="col-span-2">Type</div>
                      <div className="col-span-4">Description</div>
                      <div className="col-span-2 text-right">Amount</div>
                      <div className="col-span-2 text-right">Invoice</div>
                    </div>
                    {billingHistory.events.map((evt, idx) => (
                      <div
                        key={`k1-${idx}`}
                        data-testid={`billing-event-${idx}`}
                        className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-center border-b border-border/20 last:border-0 hover:bg-secondary/30 transition-colors"
                      >
                        <div className="col-span-2 text-muted-foreground flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(evt.date)}
                        </div>
                        <div className="col-span-2">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            evt.type === 'plan_change'
                              ? 'bg-primary/10 text-primary border border-primary/20'
                              : 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                          }`}>
                            {evt.type === 'plan_change' ? 'Plan' : 'Add-on'}
                          </span>
                        </div>
                        <div className="col-span-4 text-foreground/90">{evt.description}</div>
                        <div className="col-span-2 text-right font-medium text-foreground">
                          {evt.amount > 0 ? `$${evt.amount}` : 'Free'}
                        </div>
                        <div className="col-span-2 text-right">
                          {evt.amount > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
                              onClick={() => downloadInvoice(evt)}
                              data-testid={`download-invoice-${idx}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-1" />
                              Invoice
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-8 text-center">
                    <Receipt className="w-10 h-10 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">No billing history yet</p>
                    <p className="text-xs text-muted-foreground/70">Transactions will appear here when you upgrade or purchase add-ons</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
